import { useState, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme, MenuList, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import MoreIcon from '@mui/icons-material/MoreVertRounded'
import { useSnackbar } from 'notistack'

import { SwalContext, MenuIcon } from 'genesis-suite/components'
import { CheckedCircle, TV } from 'genesis-suite/icons'
import { widgetCreators } from '../actions/creators'
import { widgetSelectors, authSelectors, moduleSelectors } from '../selectors'
import { authCreators, moduleCreators } from '../actions/creators'
import { logEvent } from '../lib/amplitudeClient'

export default function PerspectiveToolbarMenuItems({ iconProps, buttonStyle, perspectiveLayout }) {
    const { confirm } = useContext(SwalContext)
    const { enqueueSnackbar: showSnackbar } = useSnackbar()
    const autoLaunchCatch = () => showSnackbar('An error occurred updating your auto launch.', { variant: 'error' })

    const perspective = useSelector(widgetSelectors.getCurrentPerspectiveConfig)
    const perspectiveId = perspective?.Id
    const disabled = !perspectiveId
    const isModuleAutoLaunchPerspective = useSelector(widgetSelectors.getIsPerspectiveAutoLaunch)
    const userAutoLaunchPerspective = useSelector(authSelectors.getAutoLaunchPerspective)
    const isPowerUser = useSelector(authSelectors.getIsPowerUser)
    const moduleId = useSelector(moduleSelectors.getModuleId)

    let isAutoLaunch = false
    let isAutoLaunchOverride = false
    let userAutoLaunchUpdateValue
    let userAutoLaunchUpdateResponse = 'added'
    if (isModuleAutoLaunchPerspective) {
        if (perspectiveId === userAutoLaunchPerspective || !userAutoLaunchPerspective) {
            isAutoLaunch = true
            userAutoLaunchUpdateValue = '__home'
            userAutoLaunchUpdateResponse = 'removed'
        } else {
            isAutoLaunchOverride = true
        }
    } else {
        if (perspectiveId === userAutoLaunchPerspective) {
            isAutoLaunch = true
            userAutoLaunchUpdateResponse = 'removed'
        } else {
            userAutoLaunchUpdateValue = perspectiveId
        }
    }

    const dispatch = useDispatch()
    const updateUserAutoLaunch = () =>
        dispatch(authCreators.updateAutoLaunchPerspective(userAutoLaunchUpdateValue))
            .then(() => showSnackbar(`Auto launch ${userAutoLaunchUpdateResponse}.`))
            .catch(autoLaunchCatch)

    const [open, setOpen] = useState(false)
    const theme = useTheme()

    const autoLaunchIconColor = disabled
        ? theme.palette.grayscale.light
        : isAutoLaunch
        ? theme.palette.status.success
        : isAutoLaunchOverride
        ? theme.palette.status.warning
        : theme.palette.text.primary

    const autoLaunchIconProps = { ...iconProps, style: { ...iconProps.style, color: autoLaunchIconColor } }

    const handleAutoLaunch = async () => {
        if (isPowerUser) {
            const { IsGlobal, ParentVisorId, ParentVisorName } = perspective
            const notParentModule = IsGlobal && ParentVisorId !== moduleId

            if (notParentModule) {
                const response = await confirm(`To save for all, go to the parent portal ${ParentVisorName}`, {
                    title: 'Save just for you?',
                    type: 'question',
                    showCloseButton: false,
                    confirmButtonProps: { text: 'Just me' },
                })
                if (!response.isConfirmed) return

                updateUserAutoLaunch()
            } else {
                const response = await confirm(
                    'If you update for everyone, users will still be able to set their own Auto launch override if they choose.',
                    {
                        title: 'Just you or everyone?',
                        type: 'question',
                        showCloseButton: false,
                        cancelButtonProps: { text: 'Just me' },
                        confirmButtonProps: { text: 'Everyone' },
                    }
                )
                if (response.dismiss === 'backdrop') return

                if (response.value)
                    dispatch(moduleCreators.saveAutoLaunch(perspectiveId))
                        .then(() =>
                            showSnackbar(
                                `Auto launch ${isModuleAutoLaunchPerspective ? 'removed' : 'added'} for all users.`
                            )
                        )
                        .catch(autoLaunchCatch)
                else updateUserAutoLaunch()
            }
        } else {
            updateUserAutoLaunch()
        }

        logEvent(isAutoLaunch ? 'AUTO_LAUNCH_TOGGLED_OFF' : 'AUTO_LAUNCH_TOGGLED_ON')
    }

    let items = [
        {
            name: 'TV mode',
            click: () => dispatch(widgetCreators.toggleTVMode()),
            icon: <TV {...iconProps} />,
        },
        {
            name: 'Auto launch',
            click: handleAutoLaunch,
            icon: <CheckedCircle {...autoLaunchIconProps} />,
        },
    ]

    if (perspectiveLayout) {
        items = [
            {
                name: 'Auto launch',
                click: handleAutoLaunch,
                icon: <CheckedCircle {...autoLaunchIconProps} />,
            },
        ]
    }

    return (
        <MenuIcon
            icon={<MoreIcon {...iconProps} />}
            buttonProps={{ sx: buttonStyle }}
            disabled={disabled}
            open={open}
            onClick={() => setOpen(true)}
            onClose={() => setOpen(false)}
            tooltip="More options"
            noPadding
        >
            <MenuList>
                {items.map(item => (
                    <MenuItem
                        key={item.name}
                        onClick={() => {
                            item.click()
                            setOpen(false)
                        }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText>{item.name}</ListItemText>
                    </MenuItem>
                ))}
            </MenuList>
        </MenuIcon>
    )
}
